import React from 'react';
import './Privacy.css';

const Privacy = () => {
  return (
    <div className="container">
      <div className="content">
        <h1 className="mainTitle">Privacy Policy</h1>
        <p className="effectiveDate">Effective date: Sep 19, 2025</p>

        <section className="section">
          <h2 className="sectionTitle">1. Our Role as a Platform</h2>
          <p className="sectionText">FO Platform ("Platform") operates as a technology platform that facilitates fractional real estate investments. We do not own, manage, or operate the properties listed on our platform. Our role is limited to providing a marketplace where property owners can list fractional ownership interests and investors can purchase these interests. We are not a real estate broker, investment advisor, or fiduciary. All transactions are between the property owner/seller and the investor/buyer. We do not guarantee the performance, value, or legality of any investment.</p>
        </section>

        <section className="section">
          <h2 className="sectionTitle">2. Personal Information Collected</h2>
          <p className="sectionText">We collect the following types of personal information:</p>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, phone number, date of birth, and password when you register.</li>
            <li><strong>Identity Verification:</strong> Government-issued ID, PAN card, address proof, and other KYC documents as required by regulations.</li>
            <li><strong>Financial Information:</strong> Bank account details, payment method information, and transaction history for processing investments.</li>
            <li><strong>Usage Data:</strong> IP address, browser type, device information, pages visited, and interaction data through cookies and analytics tools.</li>
            <li><strong>Communications:</strong> Messages, emails, or other communications you send to us.</li>
            <li><strong>Third-Party Data:</strong> Information from identity verification services, sanctions screening, and credit checks.</li>
          </ul>
        </section>

        <section className="section">
          <h2 className="sectionTitle">3. How We Use Your Personal Information</h2>
          <p className="sectionText">We use your personal information for the following purposes:</p>
          <ul>
            <li>To create and manage your account and provide access to the platform.</li>
            <li>To verify your identity and comply with KYC/AML regulations.</li>
            <li>To process transactions and facilitate fractional ownership purchases.</li>
            <li>To communicate with you about your account, transactions, and platform updates.</li>
            <li>To provide customer support and respond to your inquiries.</li>
            <li>To improve our platform, develop new features, and conduct analytics.</li>
            <li>To comply with legal obligations, including reporting to regulatory authorities.</li>
            <li>To prevent fraud, security breaches, and illegal activities.</li>
            <li>With your consent, to send marketing communications about our services.</li>
          </ul>
        </section>

        <section className="section">
          <h2 className="sectionTitle">4. Sharing Your Personal Information</h2>
          <p className="sectionText">We may share your personal information in the following circumstances:</p>
          <ul>
            <li><strong>Service Providers:</strong> With third-party vendors who assist us in operating the platform, such as payment processors, KYC providers, and cloud hosting services.</li>
            <li><strong>Property Owners/Sellers:</strong> Limited information necessary to complete transactions, such as your name and contact details.</li>
            <li><strong>Regulatory Authorities:</strong> As required by law, including for tax reporting, anti-money laundering compliance, and legal proceedings.</li>
            <li><strong>Legal Requirements:</strong> To respond to subpoenas, court orders, or other legal processes.</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
            <li><strong>Consent:</strong> With your explicit consent for specific purposes.</li>
          </ul>
          <p className="sectionText">We do not sell your personal information to third parties for marketing purposes.</p>
        </section>

        <section className="section">
          <h2 className="sectionTitle">5. Data Retention</h2>
          <p className="sectionText">We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. Specific retention periods include:</p>
          <ul>
            <li>Account information: Retained while your account is active and for 7 years after account closure for tax and regulatory purposes.</li>
            <li>KYC documents: Retained for 5 years after account closure or as required by regulations.</li>
            <li>Transaction records: Retained for 7 years for tax and audit purposes.</li>
            <li>Usage data: Retained for 2 years for analytics and improvement purposes.</li>
          </ul>
          <p className="sectionText">You may request deletion of your data subject to legal and regulatory limitations.</p>
        </section>

        <section className="section">
          <h2 className="sectionTitle">6. Security</h2>
          <p className="sectionText">We implement appropriate technical, administrative, and physical security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:</p>
          <ul>
            <li>Encryption of data in transit and at rest.</li>
            <li>Access controls and authentication mechanisms.</li>
            <li>Regular security audits and vulnerability assessments.</li>
            <li>Employee training on data protection practices.</li>
          </ul>
          <p className="sectionText">However, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.</p>
        </section>

        <section className="section">
          <h2 className="sectionTitle">7. Your Rights</h2>
          <p className="sectionText">Subject to applicable laws, you have the following rights regarding your personal information:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal limitations.</li>
            <li><strong>Portability:</strong> Request transfer of your data in a structured, machine-readable format.</li>
            <li><strong>Restriction:</strong> Request limitation of processing in certain circumstances.</li>
            <li><strong>Objection:</strong> Object to processing based on legitimate interests or for direct marketing.</li>
          </ul>
          <p className="sectionText">To exercise these rights, contact us using the information provided below. We may require verification of your identity before processing requests.</p>
        </section>

        <section className="section">
          <h2 className="sectionTitle">8. Minors</h2>
          <p className="sectionText">Our platform is not intended for individuals under 18 years of age. We do not knowingly collect personal information from minors. If we become aware that we have collected personal information from a minor, we will take steps to delete such information promptly.</p>
        </section>

        <section className="section">
          <h2 className="sectionTitle">9. Cookies and Tracking Technologies</h2>
          <p className="sectionText">We use cookies and similar tracking technologies to enhance your experience on our platform. This includes:</p>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for platform functionality, such as authentication and security.</li>
            <li><strong>Analytics Cookies:</strong> To understand how users interact with our platform and improve services.</li>
            <li><strong>Marketing Cookies:</strong> To provide personalized content and advertisements.</li>
          </ul>
          <p className="sectionText">You can manage your cookie preferences through your browser settings. However, disabling certain cookies may affect platform functionality.</p>
        </section>

        <section className="section">
          <h2 className="sectionTitle">10. Disclaimer</h2>
          <p className="sectionText">This Privacy Policy does not apply to third-party websites, services, or applications that may be linked from our platform. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.</p>
        </section>

        <section className="section">
          <h2 className="sectionTitle">11. Changes to This Privacy Policy</h2>
          <p className="sectionText">We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of material changes by email or through a prominent notice on our platform. Your continued use of the platform after such changes constitutes acceptance of the updated policy.</p>
        </section>

        <section className="section">
          <h2 className="sectionTitle">12. Contact Us</h2>
          <p className="sectionText">If you have any questions about this Privacy Policy or our data practices, please contact us at:</p>
          <p className="sectionText">Email: privacy@realtyseek.ai</p>
          <p className="sectionText">Phone: [Insert Phone Number]</p>
          <p className="sectionText">Address: [Insert Address]</p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
